import { useState, useEffect } from 'react';
import API from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';

const CLASS_OPTIONS = ['Pre-Nursery', 'Nursery', 'LKG', 'UKG', 'Class I', 'Class II', 'Class III', 'Class IV', 'Class V', 'Class VI', 'Class VII', 'Class VIII', 'Class IX', 'Class X', 'Class XI', 'Class XII'];

const MOBILE_RE = /^[6-9]\d{9}$/;

const Admission = () => {
  const pageData = usePage('admission');
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({ heading: '', sub: '' });
  const [loadingSections, setLoadingSections] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', phone: '', class_applying: '', message: '', enquiry_type: 'Admission Enquiry' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/home-sections/admission_sections')
      .then(res => {
        const d = res.data?.data?.content;
        if (d) {
          setMeta({ heading: d.heading || '', sub: d.sub || '' });
          const hidden = (t) => /fee structure|transport fee/i.test(t || '');
          setSections((d.items || []).filter(s => !hidden(s.title)));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingSections(false));
  }, []);

  const set = (field, value) => {
    if (field === 'phone' && value) value = value.replace(/\D/g, '').slice(0, 10);
    setForm(f => ({ ...f, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = 'Please enter student name';
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email)) e.email = 'Enter a valid email address';
    if (!MOBILE_RE.test(form.phone)) e.phone = 'Enter valid 10-digit mobile number';
    if (!form.class_applying) e.class_applying = 'Please select a class';
    if (!form.message.trim()) e.message = 'Please enter a message';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await API.post('/enquiries/public', { ...form, phone: `+91${form.phone}` });
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', class_applying: '', message: '', enquiry_type: 'Admission Enquiry' });
    } catch (err) { alert('Error submitting enquiry. Please try again.'); }
    finally { setLoading(false); }
  };

  const fieldCls = (field) => [
    'w-full px-4 py-3 border rounded-xl text-sm outline-none transition-all duration-200 bg-gray-50/60',
    errors[field] ? 'border-red-300 bg-red-50/30 focus:border-red-500 focus:ring-2 focus:ring-red-100' : 'border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/15 hover:border-gray-300',
  ].join(' ');

  return (
    <>
      <SEO
        slug="admission"
        title="Admission" description="Admission process, eligibility, and application for St. Andrews World School, Indirapuram." />
      <PageBanner title="Admission" subtitle="Join the St. Andrews family" breadcrumbs={[{ label: 'Home', link: '/' }, { label: 'Admission' }]} backgroundImage={pageData.bannerImage} fallback='${process.env.PUBLIC_URL}/images/page-banners/admission-guidliness.jpg' loading={pageData.loading} />

      {/* ADMISSIONS OPEN STRIP — from backend */}
      {meta.heading && (
        <section className="relative overflow-hidden bg-gradient-to-r from-primary via-primary-dark to-primary">
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '20px 20px' }} />
          <div className="container relative text-center py-6 md:py-8">
            <h1 className="text-white font-display font-bold text-2xl md:text-3xl">{meta.heading}</h1>
            {meta.sub && <p className="text-white/75 text-center text-sm md:text-base mt-2 max-w-2xl mx-auto">{meta.sub}</p>}
          </div>
        </section>
      )}

      {/* PAGE CONTENT + SECTION CARDS – alternating left-right */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-[1100px] mx-auto space-y-8">
            {loadingSections ? (
              <div className="text-center py-16 text-gray-400">
                <i className="fas fa-spinner fa-spin text-2xl mb-3"></i>
                <p className="text-sm">Loading admission information...</p>
              </div>
            ) : (
              sections.map((sec, i) => {
                const isRight = i % 2 === 1;
                return (
                  <Reveal key={i} delayMs={i * 60}>
                    <div className={`flex flex-col ${isRight ? 'md:flex-row-reverse' : 'md:flex-row'} bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-500 group overflow-hidden`}>
                      {/* Icon / number badge side */}
                      <div className={`flex items-center justify-center md:w-[180px] shrink-0 bg-gradient-to-br ${isRight ? 'from-primary-dark to-primary' : 'from-primary to-primary-dark'} relative overflow-hidden`}>
                        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '16px 16px' }} />
                        <div className="relative flex flex-col items-center gap-3 text-white py-8">
                          <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur border border-white/25 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                            <i className={`fas ${sec.icon || 'fa-circle'} text-2xl`}></i>
                          </div>
                          <span className="font-display font-extrabold text-4xl text-white/20">{String(i + 1).padStart(2, '0')}</span>
                          <h3 className="text-base font-bold text-white text-center px-4">{sec.title}</h3>
                        </div>
                      </div>
                      {/* Content side */}
                      <div className="flex-1 px-6 md:px-8 py-6 md:py-8 admission-section-content" dangerouslySetInnerHTML={{ __html: sec.content }} />
                    </div>
                  </Reveal>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-secondary/10 blur-3xl" />
        <div className="container relative">
          <div className="text-center mb-10">
            <Reveal>
              <div className="kicker">Get In Touch</div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-primary mt-2">Enquire About Admission</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">Fill in the form below and our admission counsellor will assist you with the complete process.</p>
            </Reveal>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Right form */}
            <Reveal>
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6 pb-5 border-b border-gray-100">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <i className="fas fa-file-signature text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary">Admission Enquiry</h3>
                    <p className="text-gray-400 text-xs">Fields marked * are required</p>
                  </div>
                </div>

                {submitted ? (
                  <div className="text-center py-14">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg shadow-green-500/30">
                      <i className="fas fa-check text-white text-3xl"></i>
                    </div>
                    <h3 className="text-primary font-bold text-2xl mb-2">Thank You!</h3>
                    <p className="text-gray-500 mb-6">Your enquiry has been submitted. Our team will contact you shortly.</p>
                    <div className="flex items-center justify-center gap-2.5">
                      <a href="tel:+919311668860" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all"><i className="fas fa-phone-alt text-xs"></i> Call Now</a>
                      <button className="text-primary font-semibold text-sm underline underline-offset-4" onClick={() => setSubmitted(false)}>Submit Another</button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="Enter your name" value={form.name} onChange={e => set('name', e.target.value)} className={fieldCls('name')} />
                        {errors.name && <p className="text-[11.5px] text-red-600 mt-1 flex items-center gap-1"><i className="fas fa-exclamation-circle text-[10px]"></i>{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Email Address <span className="text-red-500">*</span></label>
                        <input type="email" placeholder="Enter email" value={form.email} onChange={e => set('email', e.target.value)} className={fieldCls('email')} />
                        {errors.email && <p className="text-[11.5px] text-red-600 mt-1 flex items-center gap-1"><i className="fas fa-exclamation-circle text-[10px]"></i>{errors.email}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Phone Number <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500 pointer-events-none select-none">+91</span>
                          <input type="tel" inputMode="numeric" placeholder="10-digit mobile number" value={form.phone} onChange={e => set('phone', e.target.value)} maxLength={10} className={fieldCls('phone') + ' !pl-12'} />
                        </div>
                        {errors.phone && <p className="text-[11.5px] text-red-600 mt-1 flex items-center gap-1"><i className="fas fa-exclamation-circle text-[10px]"></i>{errors.phone}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Class Seeking Admission To <span className="text-red-500">*</span></label>
                        <select value={form.class_applying} onChange={e => set('class_applying', e.target.value)} className={fieldCls('class_applying') + ' bg-white cursor-pointer'}>
                          <option value="">Select a class</option>
                          {CLASS_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {errors.class_applying && <p className="text-[11.5px] text-red-600 mt-1 flex items-center gap-1"><i className="fas fa-exclamation-circle text-[10px]"></i>{errors.class_applying}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1.5">Message <span className="text-red-500">*</span></label>
                      <textarea placeholder="Any specific questions or requirements..." value={form.message} onChange={e => set('message', e.target.value)} rows={4} className={fieldCls('message') + ' resize-y'} />
                      {errors.message && <p className="text-[11.5px] text-red-600 mt-1 flex items-center gap-1"><i className="fas fa-exclamation-circle text-[10px]"></i>{errors.message}</p>}
                    </div>
                    <button type="submit" disabled={loading}
                      className="group relative w-full overflow-hidden bg-gradient-to-r from-primary to-primary-dark text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0 flex items-center justify-center gap-2">
                      <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 ease-out" />
                      <span className="relative flex items-center gap-2">
                        {loading ? (<><i className="fas fa-spinner fa-spin"></i> Submitting...</>) : (<><i className="fas fa-paper-plane"></i> Submit Enquiry</>)}
                      </span>
                    </button>
                   
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Styles for section content */}
      <style>{`
        .admission-section-content h4 {
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--color-primary, #1B3160);
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .admission-section-content ul,
        .admission-section-content ol {
          margin: 0.75rem 0;
          padding-left: 1.5rem;
        }
        .admission-section-content ul { list-style-type: disc; }
        .admission-section-content ol { list-style-type: decimal; }
        .admission-section-content li {
          margin-bottom: 0.4rem;
          line-height: 1.7;
        }
        .admission-section-content p {
          margin-bottom: 0.6rem;
          line-height: 1.7;
        }
        .admission-section-content strong {
          color: var(--color-primary, #1B3160);
        }
        .admission-section-content .note-box {
          background: #fffbeb;
          border-left: 4px solid #f59e0b;
          border-radius: 8px;
          padding: 1rem 1.25rem;
          margin: 1rem 0;
        }
        .admission-section-content .note-box h4 { margin-top: 0; }
        .admission-section-content .note-box ul { margin: 0.5rem 0; }
        .admission-section-content table {
          width: 100%;
          border-collapse: collapse;
          border-radius: 12px;
          overflow: hidden;
          margin: 1rem 0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .admission-section-content thead {
          background: var(--color-primary, #1B3160);
          color: white;
        }
        .admission-section-content th {
          padding: 0.65rem 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .admission-section-content td {
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          border-bottom: 1px solid #f3f4f6;
        }
        .admission-section-content tbody tr:nth-child(even) {
          background: #f9fafb;
        }
        .admission-section-content tbody tr:hover {
          background: #f0f4ff;
        }
      `}</style>
    </>
  );
};

export default Admission;